/**
 * AssumptionTracker — Strategic Navigation / Assumption Log
 *
 * "A new strategy is a hypothesis — an educated guess about what will work."
 *
 * When a strategy is activated, leaders must write down the key assumptions
 * they are making. The app reminds them to review (and navigate/adjust) when
 * those review dates arrive.
 *
 * Features:
 *   - List view with status badges (active / confirmed / invalidated)
 *   - Overdue review dates highlighted in amber
 *   - Inline add, edit, and status update
 *   - Review notes field
 */

import React, { useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import { useStrategyKernelStore } from '../../../stores/prioritization/strategyKernelStore';
import type { StrategyAssumption } from '../../../types/prioritization/strategyKernel';
import { getAssumptionStatusLabel } from '../../../types/prioritization/strategyKernel';

// ============================================================================
// Helpers
// ============================================================================

function isOverdue(assumption: StrategyAssumption): boolean {
  if (assumption.status !== 'active' || !assumption.reviewDate) return false;
  return assumption.reviewDate < new Date().toISOString().slice(0, 10);
}

function isDueSoon(assumption: StrategyAssumption): boolean {
  if (assumption.status !== 'active' || !assumption.reviewDate) return false;
  const today = new Date();
  const reviewDate = new Date(assumption.reviewDate);
  const diff = (reviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 14;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// Status config
// ============================================================================

const STATUS_CONFIG: Record<
  StrategyAssumption['status'],
  { label: string; classes: string; icon: React.ReactNode }
> = {
  active: {
    label: 'Active',
    classes: 'bg-[var(--slate)]/40 text-[var(--text-ter)]',
    icon: <ClockIcon className="w-3 h-3" />,
  },
  confirmed: {
    label: 'Confirmed',
    classes: 'bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]',
    icon: <CheckCircleIcon className="w-3 h-3" />,
  },
  invalidated: {
    label: 'Invalidated',
    classes: 'bg-[var(--coral)]/40 text-[var(--coral)]',
    icon: <ExclamationCircleIcon className="w-3 h-3" />,
  },
};

// ============================================================================
// Add / Edit form
// ============================================================================

interface AssumptionFormProps {
  initial?: Partial<StrategyAssumption>;
  onSave: (data: { assumptionText: string; reviewDate: string; status: StrategyAssumption['status']; reviewNotes: string }) => void;
  onCancel: () => void;
}

function AssumptionForm({ initial, onSave, onCancel }: AssumptionFormProps) {
  const [text, setText] = useState(initial?.assumptionText ?? '');
  const [reviewDate, setReviewDate] = useState(initial?.reviewDate ?? '');
  const [status, setStatus] = useState<StrategyAssumption['status']>(initial?.status ?? 'active');
  const [notes, setNotes] = useState(initial?.reviewNotes ?? '');

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">
          Assumption
          <span className="ml-1 font-normal text-[var(--text-ter)]">(what must be true for this strategy to succeed?)</span>
        </label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder='e.g. "We assume we can develop tailored solutions for this customer segment within 6 months."'
          rows={3}
          className="w-full text-sm border border-[var(--rule)] px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600 resize-none"
          autoFocus
        />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Review date</label>
          <input
            type="date"
            value={reviewDate}
            onChange={e => setReviewDate(e.target.value)}
            className="w-full text-sm border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)]"
          />
        </div>
        {initial?.status && (
          <div className="flex-1">
            <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as StrategyAssumption['status'])}
              className="w-full text-sm border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)]"
            >
              <option value="active">Active</option>
              <option value="confirmed">Confirmed</option>
              <option value="invalidated">Invalidated</option>
            </select>
          </div>
        )}
      </div>
      {initial?.status && (
        <div>
          <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Review notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What happened? Does the strategy need to navigate or adjust?"
            rows={2}
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600 resize-none"
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] px-3 py-1.5">
          Cancel
        </button>
        <button
          onClick={() => onSave({ assumptionText: text.trim(), reviewDate, status, reviewNotes: notes.trim() })}
          disabled={!text.trim()}
          className="text-sm font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed px-4 py-1.5"
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Single assumption row
// ============================================================================

interface AssumptionRowProps {
  assumption: StrategyAssumption;
}

function AssumptionRow({ assumption }: AssumptionRowProps) {
  const { updateAssumption, deleteAssumption } = useStrategyKernelStore();
  const [editing, setEditing] = useState(false);

  const overdue = isOverdue(assumption);
  const dueSoon = isDueSoon(assumption);
  const config = STATUS_CONFIG[assumption.status];

  const handleSave = useCallback(
    async (data: { assumptionText: string; reviewDate: string; status: StrategyAssumption['status']; reviewNotes: string }) => {
      await updateAssumption(assumption.id, {
        assumptionText: data.assumptionText,
        reviewDate: data.reviewDate || undefined,
        status: data.status,
        reviewNotes: data.reviewNotes || undefined,
      });
      setEditing(false);
    },
    [assumption.id, updateAssumption]
  );

  if (editing) {
    return (
      <AssumptionForm
        initial={assumption}
        onSave={handleSave}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div
      className={`group flex items-start gap-3  border px-4 py-3 ${
        overdue
          ? 'border-[var(--mustard)]/60 bg-[var(--mustard)]/20'
          : assumption.status === 'invalidated'
          ? 'border-[var(--coral)]/50 bg-[var(--coral)]/10'
          : assumption.status === 'confirmed'
          ? 'border-[var(--green)]/50 bg-[var(--green)]/10'
          : 'border-[var(--rule)] bg-[var(--bg-raised)]'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <p className={`text-sm ${assumption.status === 'invalidated' ? 'text-[var(--coral)] line-through' : 'text-[var(--text-ter)]'}`}>
            {assumption.assumptionText}
          </p>
        </div>

        {assumption.reviewNotes && (
          <p className="mt-1 text-xs text-[var(--text-ter)] italic">{assumption.reviewNotes}</p>
        )}

        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {/* Status badge */}
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.classes}`}>
            {config.icon}
            {config.label}
          </span>

          {/* Review date */}
          <span className={`flex items-center gap-1 text-xs ${
            overdue ? 'text-[var(--mustard)] font-medium' :
            dueSoon ? 'text-[var(--text-ter)]' :
            'text-[var(--text-ter)]'
          }`}>
            {overdue ? (
              <ExclamationTriangleIcon className="w-3 h-3 shrink-0" />
            ) : (
              <CalendarIcon className="w-3 h-3 shrink-0" />
            )}
            Review: {formatDate(assumption.reviewDate)}
            {overdue && ' — OVERDUE'}
            {dueSoon && !overdue && ' — Due soon'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {assumption.status === 'active' && (
          <>
            <button
              onClick={() => updateAssumption(assumption.id, { status: 'confirmed' })}
              className="text-[var(--text-sec)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] transition-colors"
              title="Mark as confirmed"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateAssumption(assumption.id, { status: 'invalidated' })}
              className="text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors"
              title="Mark as invalidated"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </>
        )}
        {assumption.status !== 'active' && (
          <button
            onClick={() => updateAssumption(assumption.id, { status: 'active' })}
            className="text-xs text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors px-1"
            title="Reactivate"
          >
            Reactivate
          </button>
        )}
        <button
          onClick={() => setEditing(true)}
          className="text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors"
          title="Edit"
        >
          <PencilIcon className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => deleteAssumption(assumption.id)}
          className="text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors"
          title="Delete"
        >
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Top-level component
// ============================================================================

interface AssumptionTrackerProps {
  kernelId: string;
}

export function AssumptionTracker({ kernelId }: AssumptionTrackerProps) {
  const { getAssumptionsForKernel, getOverdueAssumptions, createAssumption } = useStrategyKernelStore();
  const [adding, setAdding] = useState(false);

  const assumptions = getAssumptionsForKernel(kernelId);
  const overdueList = getOverdueAssumptions(kernelId);

  const sortedAssumptions = [...assumptions].sort((a, b) => {
    const aOver = isOverdue(a) ? 0 : 1;
    const bOver = isOverdue(b) ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    return (a.reviewDate ?? '9999') < (b.reviewDate ?? '9999') ? -1 : 1;
  });

  const handleAdd = useCallback(
    async (data: { assumptionText: string; reviewDate: string }) => {
      await createAssumption({
        kernelId,
        assumptionText: data.assumptionText,
        reviewDate: data.reviewDate || undefined,
      });
      setAdding(false);
    },
    [kernelId, createAssumption]
  );

  // Suppress unused import warning — getAssumptionStatusLabel is exported from the types module
  void getAssumptionStatusLabel;

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="text-sm text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
        <p className="font-medium text-[var(--text-ter)] mb-1">Strategic Navigation Log</p>
        <p>A good strategy is a hypothesis. Write down the key assumptions this strategy is built on. Set review dates so leadership is forced to navigate and adjust if real-world events prove the assumptions wrong.</p>
      </div>

      {/* Overdue alert */}
      {overdueList.length > 0 && (
        <div className="flex items-start gap-2 text-sm text-[var(--mustard)] bg-[var(--mustard)]/20 border border-[var(--mustard)]/50 px-4 py-3">
          <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>{overdueList.length} assumption{overdueList.length > 1 ? 's' : ''} overdue for review.</strong>
            {' '}Examine whether the real world has disproved them. If so, your strategy needs to navigate.
          </div>
        </div>
      )}

      {/* Assumptions list */}
      <div className="space-y-2">
        {sortedAssumptions.map(assumption => (
          <AssumptionRow key={assumption.id} assumption={assumption} />
        ))}
      </div>

      {/* Add */}
      {adding ? (
        <AssumptionForm
          onSave={handleAdd}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--bg-raised)] px-3 py-1.5 border border-[var(--rule)] transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Record assumption
        </button>
      )}

      {assumptions.length === 0 && !adding && (
        <p className="text-sm text-[var(--text-ter)] italic">
          No assumptions recorded yet. Every strategy is built on assumptions — make them explicit.
        </p>
      )}
    </div>
  );
}
